import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, User, LogOut } from "lucide-react";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = location.pathname.startsWith("/preview/");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Minimal navbar on preview page — logo only, zero distractions
  if (isPreview) {
    return (
      <nav className="sticky top-0 z-50 w-full border-b border-white/10" style={{ backgroundColor: "var(--color-navy)" }} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-heading text-2xl tracking-wider text-white">
              CARD MY <span style={{ color: "var(--color-gold)" }}>DOG</span>
            </span>
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className="sticky top-0 z-50 w-full border-b border-white/10"
      style={{ backgroundColor: "var(--color-navy)" }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-heading text-2xl tracking-wider text-white">
            CARD MY <span style={{ color: "var(--color-gold)" }}>DOG</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/create" className="text-white/80 hover:text-white font-body text-sm transition-colors" data-testid="nav-create">
            Create Card
          </Link>
          <Link to="/faq" className="text-white/80 hover:text-white font-body text-sm transition-colors" data-testid="nav-faq">
            FAQ
          </Link>
          <Link to="/contact" className="text-white/80 hover:text-white font-body text-sm transition-colors" data-testid="nav-contact">
            Contact
          </Link>

          {user ? (
            <div className="flex items-center gap-4">
              <Link
                to="/account"
                className="flex items-center gap-2 text-white/80 hover:text-white font-body text-sm transition-colors"
                data-testid="nav-account"
              >
                <User size={16} />
                My Cards
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 text-white/60 hover:text-white font-body text-sm transition-colors"
                data-testid="nav-logout"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="text-white/80 hover:text-white font-body text-sm transition-colors"
              data-testid="nav-login"
            >
              Sign In
            </Link>
          )}

          <Link
            to="/create"
            className="font-heading tracking-widest uppercase text-sm px-5 py-2 rounded-[8px] transition-colors"
            style={{
              backgroundColor: "var(--color-gold)",
              color: "var(--color-navy)",
            }}
            data-testid="nav-cta"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 pb-4" style={{ backgroundColor: "var(--color-navy)" }}>
          <div className="flex flex-col px-4 gap-3 pt-3">
            <Link to="/create" onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white font-body py-2">Create Card</Link>
            <Link to="/faq" onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white font-body py-2">FAQ</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white font-body py-2">Contact</Link>
            {user ? (
              <>
                <Link to="/account" onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white font-body py-2">My Cards</Link>
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="text-white/60 hover:text-white font-body py-2 text-left">Sign Out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white font-body py-2">Sign In</Link>
            )}
            <Link
              to="/create"
              onClick={() => setMobileOpen(false)}
              className="font-heading tracking-widest uppercase text-center py-3 rounded-[8px] mt-2"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
